import React from "react";
import FeatherIcon from "feather-icons-react";

export const STATUS_META = Object.freeze({
  DRAFT: { key: "DRAFT", label: "Draft", icon: "edit-3" },
  SUBMITTED: { key: "SUBMITTED", label: "Submitted", icon: "send" },
  KYC_PENDING: { key: "KYC_PENDING", label: "KYC pending", icon: "user-check" },
  KYC_COMPLETE: { key: "KYC_COMPLETE", label: "KYC complete", icon: "check-circle" },
  IN_REVIEW: { key: "IN_REVIEW", label: "In review", icon: "search" },
  NEEDS_CLARIFICATION: { key: "NEEDS_CLARIFICATION", label: "Needs clarification", icon: "alert-circle" },
  APPROVED: { key: "APPROVED", label: "Approved", icon: "check-circle" },
  ACCOUNT_PROVISIONED: { key: "ACCOUNT_PROVISIONED", label: "Account provisioned", icon: "check" },
  REJECTED: { key: "REJECTED", label: "Rejected", icon: "x-circle" },
  DECLINED: { key: "DECLINED", label: "Declined", icon: "slash" },
  EXPIRED: { key: "EXPIRED", label: "Expired", icon: "clock" },
  WITHDRAWN: { key: "WITHDRAWN", label: "Withdrawn", icon: "archive" },
  NOT_GENERATED: { key: "NOT_GENERATED", label: "Not started", icon: "minus-circle" },
  GENERATED: { key: "GENERATED", label: "Generated", icon: "link" },
  SENT: { key: "SENT", label: "Sent", icon: "mail" },
  OPENED: { key: "OPENED", label: "Opened", icon: "external-link" },
  IN_PROGRESS: { key: "IN_PROGRESS", label: "In progress", icon: "loader" },
  REDO_REQUESTED: { key: "REDO_REQUESTED", label: "Redo requested", icon: "rotate-ccw" },
  COMPLETED: { key: "COMPLETED", label: "Completed", icon: "check-circle" },
  FAILED: { key: "FAILED", label: "Failed", icon: "alert-triangle" },
  0: { key: "DRAFT", label: "Draft", icon: "edit-3" },
  1: { key: "SUBMITTED", label: "Submitted", icon: "send" },
  2: { key: "NEEDS_CLARIFICATION", label: "Needs clarification", icon: "alert-circle" },
  3: { key: "APPROVED", label: "Approved", icon: "check-circle" },
  4: { key: "REJECTED", label: "Rejected", icon: "x-circle" },
  5: { key: "IN_REVIEW", label: "In review", icon: "search" },
  6: { key: "ACCOUNT_PROVISIONED", label: "Account provisioned", icon: "check" },
  // 7 = ACCOUNT_CREATION_FAILED: a downstream FV-side provisioning failure after
  // approval, not a broker clarification request. Surfaced broker-side as still
  // "In review" (buckets to Submitted), consistent with the application detail page.
  7: { key: "IN_REVIEW", label: "In review", icon: "search" },
  8: { key: "DECLINED", label: "Declined", icon: "slash" },
  9: { key: "EXPIRED", label: "Expired", icon: "archive" },
});

export const getStatusMeta = (status) => {
  if (status === null || status === undefined || status === "") return STATUS_META.DRAFT;
  return STATUS_META[status] || STATUS_META[String(status).toUpperCase()] || {
    key: String(status).toUpperCase(),
    label: String(status),
    icon: "circle",
  };
};

// Four broker-facing buckets — mirrors the status filter dropdown. Raw FV Bank
// status codes collapse into Incomplete / Submitted / Onboarded / Rejected.
export const STATUS_BUCKET_META = Object.freeze({
  INCOMPLETE: { key: "INCOMPLETE", label: "Incomplete", icon: "edit-3" },
  SUBMITTED: { key: "SUBMITTED", label: "Submitted", icon: "send" },
  ONBOARDED: { key: "ONBOARDED", label: "Onboarded", icon: "check-circle" },
  REJECTED: { key: "REJECTED", label: "Rejected", icon: "x-circle" },
  DECLINED: { key: "DECLINED", label: "Declined", icon: "slash" },
});

const STATUS_CODE_BUCKET = Object.freeze({
  0: "INCOMPLETE", 2: "INCOMPLETE",
  1: "SUBMITTED", 3: "SUBMITTED", 5: "SUBMITTED", 7: "SUBMITTED",
  6: "ONBOARDED", 9: "ONBOARDED",
  4: "REJECTED", 8: "DECLINED",
  DRAFT: "INCOMPLETE",
  INCOMPLETE: "INCOMPLETE",
  NEEDS_CLARIFICATION: "INCOMPLETE",
  ASK_FOR_INFO: "INCOMPLETE",
  ASK_MORE_INFO: "INCOMPLETE",
  SUBMITTED: "SUBMITTED",
  APPROVED: "SUBMITTED",
  IN_REVIEW: "SUBMITTED",
  ACCOUNT_REQUESTED: "SUBMITTED",
  ACCOUNT_CREATION_FAILED: "SUBMITTED",
  ACCOUNT_UPDATE_FAILED: "SUBMITTED",
  ONBOARDED: "ONBOARDED",
  ACCOUNT_CREATED: "ONBOARDED",
  ACCOUNT_PROVISIONED: "ONBOARDED",
  ACCOUNT_UPDATED: "ONBOARDED",
  ARCHIVED: "ONBOARDED",
  REJECTED: "REJECTED",
  DECLINED: "DECLINED",
  WITHDRAWN: "REJECTED",
});

const normalizeStatusBucketKey = (status) => String(status || "")
  .trim()
  .replace(/([a-z])([A-Z])/g, "$1_$2")
  .replace(/[^A-Za-z0-9]+/g, "_")
  .replace(/^_+|_+$/g, "")
  .toUpperCase();

export const getStatusBucketMeta = (status) => {
  if (status === null || status === undefined || status === "") return STATUS_BUCKET_META.INCOMPLETE;
  const bucket = STATUS_CODE_BUCKET[String(status)] || STATUS_CODE_BUCKET[status] || STATUS_CODE_BUCKET[normalizeStatusBucketKey(status)];
  return STATUS_BUCKET_META[bucket] || STATUS_BUCKET_META.INCOMPLETE;
};

const StatusBadge = ({ status, className = "", bucket = false }) => {
  const meta = bucket ? getStatusBucketMeta(status) : getStatusMeta(status);
  return (
    <span className={`broker-status broker-status--${meta.key.toLowerCase()} ${className}`} aria-label={`Status: ${meta.label}`}>
      <FeatherIcon icon={meta.icon} size={13} aria-hidden="true" />
      <span>{meta.label}</span>
    </span>
  );
};

export default StatusBadge;
